// Copyright 2024 Sadiant Inc. All Rights Reserved. This software is subject to a license agreement. Unauthorized or unlicensed use is prohibited.
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TSFixMe } from '@sadiant/models';
import { ToastService } from '@sadiant/toast';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AppInsightsService } from '@sadiant/azure-app-insights';

/** Set on a request when the page shows its own error message.
    Used by public pages opened from an email, where a red developer-style
    toast on top of a friendly message only confuses the reader. */
export const BYPASS_GLOBAL_ERROR_TOAST_TOKEN = new HttpContextToken(() => false);

@Injectable()
export class ApiErrorHandlerInterceptor implements HttpInterceptor {
  constructor(private toastService: ToastService, private applicationInsights: AppInsightsService) {}
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<TSFixMe>> {
    return next.handle(request).pipe(
      tap(success => {
        try {
          if (success instanceof HttpResponse && success.body?.errors) {
            (success.body.errors as string[]).forEach(err => this.toastService.showToastInfoMessage(err));
          }
        } catch (e) {
          this.traceEvents('', e);
          console.error(e);
        }
      }),
      catchError(error => {
        if (
          error instanceof HttpErrorResponse &&
          (error.status === 401 || error.url?.includes('account/refresh-token') || error.url?.includes('account/revoke-token'))
        ) {
          return next.handle(request);
        } else {
          try {
            let errorMessage = error.error.message ? error.error.message : error.message ? error.message : error.error;
            /** Still logged to App Insights below - only the toast is skipped. */
            if (request.context.get(BYPASS_GLOBAL_ERROR_TOAST_TOKEN)) {
              this.traceEvents(errorMessage, error);
              return throwError(error);
            }
            if (error.error instanceof ErrorEvent) {
              this.toastService.showToastErrorMessage(`${error.error.message}`);
            } else {
              if (error?.error?.message || error?.error?.errors) {
                if (error?.error?.message) {
                  this.toastService.showToastErrorMessage(error?.error?.message);
                }
                if (error?.error?.errors && Object.keys(error.error.errors).length) {
                  Object.values<string>(error.error.errors).forEach(error => this.toastService.showToastErrorMessage(error));
                }
              } else {
                this.toastService.showToastErrorMessage('Something went wrong');
              }
            }
            //prepare the insights
            this.traceEvents(errorMessage, error);
          } catch (e) {
            console.error(e);
            //prepare the insights
            this.traceEvents('', e);
            this.toastService.showToastErrorMessage('Something went wrong');
          }
          return throwError(error);
        }
      })
    );
  }
  traceEvents(errorMessage: string, error: any) {
    //prepare the insights
    if (!errorMessage) errorMessage = JSON.stringify(error);
    this.applicationInsights.logTrace(errorMessage, { eventId: 'login error in client app', identity: 'LOGIN_UNEXPECTED_ERROR' });
  }
}
